import { useDndContext, useDraggable, useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { FolderIconClosed, FolderIconOpen, ChevronRightIcon } from '@/utils/icons';
import { useFolderNavContext } from './FolderNav.context';
import { NewFolderNode } from './NewFolderNode';
import { PendingSpinner } from '../PendingSpinner/PendingSpinner';
import type { FolderNode } from '../utils/buildTree';
import styles from './FolderNav.module.css';
import { isImeComposing } from '@utils/ime';

function FolderIconEl({ open }: { open?: boolean }) {
  const Icon = open ? FolderIconOpen : FolderIconClosed;
  return (
    <span className={styles.folderIcon} aria-hidden="true" data-open={open ? 'true' : 'false'}>
      <Icon size={16} />
    </span>
  );
}

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <span className={`${styles.chevron} ${expanded ? styles.chevronOpen : ''}`} aria-hidden="true">
      <ChevronRightIcon size={12} />
    </span>
  );
}

interface ITreeNodeProps {
  node: FolderNode;
  depth: number;
}

export function TreeNode({ node, depth }: ITreeNodeProps) {
  const {
    selectedId, expanded, renamingId, renameValue, renameError, invalidDropId,
    newFolder, newFolderName, newFolderError,
    onSelect, onToggle, onContextMenu,
    onRenameChange, onRenameCommit, onRenameCancel,
    onNewFolderChange, onNewFolderCommit, onNewFolderCancel,
  } = useFolderNavContext();

  const isActive = selectedId === node.id;
  const isRejected = invalidDropId === node.id;
  const isExpanded = expanded.has(node.id);
  const hasChildren = node.children.length > 0;
  const isRenaming = renamingId === node.id;
  const newFolderHere = newFolder && newFolder.parentId === node.id ? newFolder : null;

  const isVirtualRoot = node.id === 'all';
  // Being renamed or moved, or under a folder that is: nothing can open, pick
  // up or drop into it until its new id arrives.
  const isSaving = node.saving;

  // System roots are valid drop *targets* (you can drop a folder into them) but
  // can't themselves be picked up — they're fixed where the server put them.
  const { setNodeRef: setDragRef, attributes, listeners, transform, isDragging } = useDraggable({
    id: node.id,
    data: { type: 'folder' },
    disabled: isVirtualRoot || isRenaming || node.isSystem || isSaving,
  });

  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: node.id, disabled: isSaving });

  // Combine drag + drop refs onto the same element.
  function setRef(el: HTMLElement | null) {
    setDragRef(el);
    setDropRef(el);
  }

  const { active } = useDndContext();
  const isDragActive = active !== null;

  const indentStyle = {
    paddingLeft: `calc(var(--s-3) + ${depth * 16}px)`,
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.4 : 1,
  };

  function handleClick() {
    if (isActive) return;
    onSelect(node.id);
  }

  const sharedInner = (
    <>
      <span
        className={styles.chevronWrap}
        onClick={hasChildren ? (e) => { e.stopPropagation(); onToggle(node.id); } : undefined}
      >
        {hasChildren && <ChevronIcon expanded={isExpanded} />}
      </span>
      <FolderIconEl open={isActive || (isExpanded && hasChildren)} />
    </>
  );

  const dropHighlight = isDragActive && isOver && !isDragging;

  return (
    <div>
      {isRenaming ? (
        <div
          ref={setRef}
          className={`${styles.item} ${isActive ? styles.active : ''}`}
          style={indentStyle}
        >
          {sharedInner}
          <span className={styles.renameField}>
            <input
              className={`${styles.renameInput} ${renameError ? styles.renameInputError : ''}`}
              value={renameValue}
              autoFocus
              aria-invalid={renameError ? true : undefined}
              onFocus={(e) => e.target.select()}
              onChange={(e) => onRenameChange(e.target.value)}
              onBlur={onRenameCommit}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isImeComposing(e)) { e.preventDefault(); onRenameCommit(); }
                if (e.key === 'Escape') { e.preventDefault(); onRenameCancel(); }
              }}
              onClick={(e) => e.stopPropagation()}
            />
            {renameError && <span className={styles.renameError} role="alert">{renameError}</span>}
          </span>
        </div>
      ) : (
        <button
          ref={setRef}
          type="button"
          className={`${styles.item} ${isActive ? styles.active : ''} ${dropHighlight ? styles.dropTarget : ''} ${isRejected ? styles.dragRejected : ''} ${isSaving ? styles.itemSaving : ''}`}
          style={indentStyle}
          disabled={isSaving}
          aria-busy={isSaving || undefined}
          onClick={handleClick}
          onContextMenu={(e) => onContextMenu(e, node.id)}
          aria-current={isActive ? 'page' : undefined}
          // The page tour's open-a-folder step drives the FIRST real folder;
          // the virtual root is not one.
          data-tour={isVirtualRoot ? undefined : 'documents-folder'}
          {...attributes}
          {...listeners}
        >
          {sharedInner}
          <span className={styles.label}>{node.label}</span>
          {isSaving && <PendingSpinner />}
        </button>
      )}

      {(hasChildren || newFolderHere) && isExpanded && (
        <div>
          {node.children.map((child) => (
            <TreeNode key={child.id} node={child} depth={depth + 1} />
          ))}
          {newFolderHere && (
            <NewFolderNode
              newFolder={newFolderHere}
              depth={depth + 1}
              value={newFolderName}
              error={newFolderError}
              onChange={onNewFolderChange}
              onCommit={onNewFolderCommit}
              onCancel={onNewFolderCancel}
            />
          )}
        </div>
      )}
    </div>
  );
}
